import React, { useEffect, useState } from 'react';
import { getKabulListesi, getKabulAyrintiListesi, kabulAyrintiEkle, kabulAyrintiGuncelle, getTarife } from '../services/api';
import HizmetEkleModal from '../components/HizmetEkleModal';
import SayiDuzenleModal from '../components/SayiDuzenleModal';
import KumulatifOzet from '../components/KumulatifOzet';
import YeniKabulModal from '../components/YeniKabulModal';
import { grupRenkleri, stokRengi } from '../utils/renkler';
import { paraFormat, hesaplaBirimFiyat, hesaplaKumulatif } from '../utils/fiyat';

// Satır zemin rengi: L8='STOK' olan kayıtlar (stok kaynaklı) sabit stokRengi, diğerleri (hizmet)
// GRUPKOD'a göre grupRenkleri — kullanıcı talebi: 2026-08-09 (Kabul İşlemleri'ne artık stok da
// eklenebildiği için stok satırları kendi sabit rengiyle ayırt ediliyor).
const satirRengi = (a) => a.l8 === 'STOK' ? stokRengi : (grupRenkleri[a.grupKod] || 'transparent');

// Ayrıntı listesini tablo yapısında gösteren ortak th/td stilleri — VB6 uygulamasındaki çizgili tablo
// görünümüne benzetmek için (kullanıcı talebi: 2026-08-09).
const tabloBaslikStil = { textAlign: 'left', padding: '7px 12px', background: '#f8faf9', border: '1px solid var(--border)', fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 };
const tabloHucreStil = { padding: '8px 12px', border: '1px solid var(--border)', fontSize: 13 };

export default function KabulIslemleri() {
  const [liste, setListe] = useState([]);
  const [yukleniyor, setYukleniyor] = useState(true);

  const [seciliKabul, setSeciliKabul] = useState(null);
  const [ayrintiListe, setAyrintiListe] = useState([]);
  const [ayrintiYukleniyor, setAyrintiYukleniyor] = useState(false);
  const [ekleModalAcik, setEkleModalAcik] = useState(false);
  const [yeniKabulAcik, setYeniKabulAcik] = useState(false);

  // Hastanın KABUL.KABUL_TARFKOD'una karşılık gelen tarife — ayrı bir seçim yok, otomatik belirlenir
  // ve B.Fiyat hesabında (hizmet formülünde) kullanılır (kullanıcı talebi: 2026-08-09).
  const [kabulTarifesi, setKabulTarifesi] = useState(null);

  // Adet/B.Fiyat düzenleme popup'ı: tip 'adet' | 'fiyat', hangi satır (index) ve o an yazılan değer.
  // KABULAYR'da tekil bir birincil anahtar olmadığından, güncelleme backend'e satırın listedeki
  // index'iyle gönderilir (bkz. KabulController.AyrintiGuncelle) — kullanıcı talebi: 2026-08-09.
  const [editModal, setEditModal] = useState(null);

  const yukle = async () => {
    setYukleniyor(true);
    try {
      const res = await getKabulListesi();
      setListe(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setYukleniyor(false);
    }
  };

  useEffect(() => { yukle(); }, []);

  const tarihFormat = (tar) => {
    if (!tar) return '';
    return new Date(tar).toLocaleDateString('tr-TR');
  };

  const ayrintiYukle = async (masterProtNo) => {
    setAyrintiYukleniyor(true);
    try {
      const res = await getKabulAyrintiListesi(masterProtNo);
      setAyrintiListe(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setAyrintiYukleniyor(false);
    }
  };

  const ayrintiAc = (kabul) => {
    setSeciliKabul(kabul);
    setKabulTarifesi(null);
    ayrintiYukle(kabul.masterProtNo);
    if (kabul.kabulTarfKod) {
      getTarife(kabul.kabulTarfKod).then(res => setKabulTarifesi(res.data)).catch(err => console.error(err));
    }
  };

  // Yeni Kabul Yap başarılı olunca: listeyi tazele ve az önce oluşturulan protokolün ayrıntı
  // görünümüne geç — kullanıcı akışı doğal olarak "kabul yap, sonra hizmet ekle" şeklinde devam eder
  // (kullanıcı talebi: 2026-08-09).
  const kabulYapildi = async (masterProtNo) => {
    setYeniKabulAcik(false);
    const res = await getKabulListesi();
    setListe(res.data);
    const yeniKabul = res.data.find(k => k.masterProtNo === masterProtNo);
    if (yeniKabul) ayrintiAc(yeniKabul);
  };

  const geriDon = () => {
    setSeciliKabul(null);
    setAyrintiListe([]);
    setKabulTarifesi(null);
  };

  const hizmetlerKaydet = async (kayitlar) => {
    const kullanici = JSON.parse(localStorage.getItem('kullanici') || '{}');
    const kalem = k => ({ kod: k.kod, adet: k.adet ?? 1, fiat1: hesaplaBirimFiyat(k, kabulTarifesi) });
    await kabulAyrintiEkle({
      masterProtNo: seciliKabul.masterProtNo,
      kayitNo: seciliKabul.kayitNo,
      whoNo: kullanici.noo,
      who: kullanici.adSoyad,
      hizmetler: kayitlar.filter(k => k.kaynak !== 'stok').map(kalem),
      stoklar: kayitlar.filter(k => k.kaynak === 'stok').map(kalem),
    });
    setEkleModalAcik(false);
    ayrintiYukle(seciliKabul.masterProtNo);
  };

  // --- Adet düzenleme: sadece rakam, en fazla 3 hane (max 999) ---

  const adetDuzenlemeyeBasla = (e, i) => {
    e.stopPropagation();
    setEditModal({ tip: 'adet', index: i, deger: String(ayrintiListe[i].l2 || 0) });
  };

  const adetDegisti = (e) => {
    const sadeceRakam = e.target.value.replace(/\D/g, '').slice(0, 3);
    setEditModal(prev => ({ ...prev, deger: sadeceRakam }));
  };

  const adetKaydet = async () => {
    const yeniAdet = parseInt(editModal.deger, 10);
    const finalAdet = (Number.isFinite(yeniAdet) && yeniAdet > 0) ? yeniAdet : 1;
    const satir = ayrintiListe[editModal.index];
    try {
      await kabulAyrintiGuncelle({ masterProtNo: seciliKabul.masterProtNo, index: editModal.index, adet: finalAdet, fiat1: satir.fiat1 || 0 });
    } catch (err) {
      console.error(err);
    }
    setEditModal(null);
    ayrintiYukle(seciliKabul.masterProtNo);
  };

  // --- B.Fiyat düzenleme: TL formatında (1.234.567,89), tam kısım en fazla 7 hane (max 9.999.999) ---

  const fiyatDuzenlemeyeBasla = (e, i) => {
    e.stopPropagation();
    setEditModal({ tip: 'fiyat', index: i, deger: paraFormat(ayrintiListe[i].fiat1 || 0) });
  };

  const fiyatDegisti = (e) => {
    const girdi = e.target.value.replace(/[^\d,]/g, '');
    const virgulIndex = girdi.indexOf(',');
    const tamKisimHam = (virgulIndex === -1 ? girdi : girdi.slice(0, virgulIndex)).slice(0, 7);
    const kusuratHam = (virgulIndex === -1 ? '' : girdi.slice(virgulIndex + 1).replace(/,/g, '')).slice(0, 2);

    const tamFormatli = tamKisimHam === ''
      ? (virgulIndex !== -1 ? '0' : '')
      : Number(tamKisimHam).toLocaleString('tr-TR');

    const yeniDeger = virgulIndex === -1 ? tamFormatli : `${tamFormatli},${kusuratHam}`;
    setEditModal(prev => ({ ...prev, deger: yeniDeger }));
  };

  const fiyatKaydet = async () => {
    const normalize = editModal.deger.replace(/\./g, '').replace(',', '.');
    const deger = parseFloat(normalize);
    const finalFiat = (Number.isFinite(deger) && deger >= 0) ? deger : 0;
    const satir = ayrintiListe[editModal.index];
    try {
      await kabulAyrintiGuncelle({ masterProtNo: seciliKabul.masterProtNo, index: editModal.index, adet: satir.l2 || 1, fiat1: finalFiat });
    } catch (err) {
      console.error(err);
    }
    setEditModal(null);
    ayrintiYukle(seciliKabul.masterProtNo);
  };

  if (seciliKabul) {
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button className="btn btn-secondary" onClick={geriDon} style={{ fontSize: 12, padding: '6px 12px' }}>◀ Geri</button>
            <h2 style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>
              Protokol #{seciliKabul.masterProtNo} · {seciliKabul.adSoy}
            </h2>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" style={{ fontSize: 12, padding: '6px 12px' }} onClick={() => setEkleModalAcik(true)}>➕ Ekle</button>
            <button className="btn" style={{ fontSize: 12, padding: '6px 12px', background: '#c0392b', color: '#fff', border: 'none' }}>🗑 Sil</button>
          </div>
        </div>

        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>
          Tarife: {kabulTarifesi ? kabulTarifesi.tarifeAdi : '—'}
        </div>

        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {ayrintiYukleniyor ? (
            <div className="loading">Yükleniyor...</div>
          ) : ayrintiListe.length === 0 ? (
            <div className="empty">Kayıt bulunamadı</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ ...tabloBaslikStil, width: 100 }}>İşlem Tarihi</th>
                    <th style={tabloBaslikStil}>Yapılan İşlemler</th>
                    <th style={{ ...tabloBaslikStil, textAlign: 'right', width: 55 }}>Adet</th>
                    <th style={{ ...tabloBaslikStil, textAlign: 'right', width: 100 }}>B.Fiyat</th>
                    <th style={{ ...tabloBaslikStil, textAlign: 'right', width: 100 }}>Tutar</th>
                  </tr>
                </thead>
                <tbody>
                  {ayrintiListe.map((a, i) => (
                    <tr key={i} style={{ background: satirRengi(a) }}>
                      <td style={tabloHucreStil}>{tarihFormat(a.tar)}</td>
                      <td style={tabloHucreStil}>{a.l1}</td>

                      <td onClick={e => adetDuzenlemeyeBasla(e, i)} style={{ ...tabloHucreStil, textAlign: 'right', textDecoration: 'underline dotted', cursor: 'pointer' }}>
                        {a.l2}
                      </td>

                      <td onClick={e => fiyatDuzenlemeyeBasla(e, i)} style={{ ...tabloHucreStil, textAlign: 'right', textDecoration: 'underline dotted', cursor: 'pointer' }}>
                        {paraFormat(a.fiat1 || 0)}
                      </td>

                      <td style={{ ...tabloHucreStil, textAlign: 'right', fontWeight: 500 }}>{paraFormat((a.l2 || 0) * (a.fiat1 || 0))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {ayrintiListe.length > 0 && (
          <KumulatifOzet {...hesaplaKumulatif(ayrintiListe.map(a => ({ birimFiyat: a.fiat1, kdvYd: a.kdvYd })))} />
        )}

        {ekleModalAcik && (
          <HizmetEkleModal
            onKapat={() => setEkleModalAcik(false)}
            onKaydet={hizmetlerKaydet}
          />
        )}

        {editModal?.tip === 'adet' && (
          <SayiDuzenleModal
            baslik="Adet"
            deger={editModal.deger}
            onDeger={adetDegisti}
            onTamam={adetKaydet}
            onKapat={() => setEditModal(null)}
            numerik="tam"
          />
        )}

        {editModal?.tip === 'fiyat' && (
          <SayiDuzenleModal
            baslik="Birim Fiyat"
            deger={editModal.deger}
            onDeger={fiyatDegisti}
            onTamam={fiyatKaydet}
            onKapat={() => setEditModal(null)}
            numerik="ondalik"
          />
        )}
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <h2 style={{ fontSize: 14, fontWeight: 600 }}>Kabul İşlemleri</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-primary" onClick={() => setYeniKabulAcik(true)} style={{ fontSize: 12, padding: '6px 12px' }}>➕ YENİ KABUL YAP</button>
          <button className="btn btn-secondary" onClick={yukle} style={{ fontSize: 12, padding: '6px 12px' }}>🔄 Yenile</button>
        </div>
      </div>

      {yeniKabulAcik && (
        <YeniKabulModal onKapat={() => setYeniKabulAcik(false)} onKabulYapildi={kabulYapildi} />
      )}

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {yukleniyor ? (
          <div className="loading">Yükleniyor...</div>
        ) : liste.length === 0 ? (
          <div className="empty">Kayıt bulunamadı</div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '110px 100px 90px 1fr', padding: '6px 12px', background: '#f8faf9', borderBottom: '1px solid var(--border)', fontSize: 11, color: 'var(--text-muted)', fontWeight: 500 }}>
              <span>Tarih</span>
              <span>Protokol</span>
              <span>Hasta No</span>
              <span>Hasta Adı Soyadı</span>
            </div>

            {liste.map(k => (
              <div
                key={k.masterProtNo}
                style={{ display: 'grid', gridTemplateColumns: '110px 100px 90px 1fr', padding: '8px 12px', borderBottom: '1px solid var(--border)', alignItems: 'center', fontSize: 13 }}
              >
                <span>{tarihFormat(k.tar)}</span>
                <span
                  onClick={() => ayrintiAc(k)}
                  style={{ color: 'var(--med-accent)', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}
                >
                  {k.masterProtNo}
                </span>
                <span>{k.kayitNo}</span>
                <span style={{ fontWeight: 500 }}>{k.adSoy}</span>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
